/**
 * 전역 confirm / prompt dialog store — native confirm() · prompt() 대체.
 *
 *   await useConfirm({ title, message })        → boolean
 *   await usePrompt({ title, inputLabel, ... }) → string | null   (취소면 null)
 *
 * usePrompt 는 native prompt() 로 받던 날짜·금액·영수증번호를 타입과 검증을 갖춰
 * 받기 위한 것이다 (prompt 는 아무 문자열이나 그대로 통과시킨다).
 */
import { defineStore } from 'pinia';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'destructive' 면 confirm 버튼 critical 색상 (Delete / Revoke 등) */
  variant?: 'primary' | 'destructive';
}

export type PromptInputType = 'text' | 'textarea' | 'number' | 'date' | 'month';

export interface PromptOptions extends ConfirmOptions {
  inputLabel: string;
  inputType?: PromptInputType;
  placeholder?: string;
  initial?: string;
  /** 비어 있으면 확인 버튼이 비활성 */
  required?: boolean;
  maxLength?: number;
  /** number 전용 */
  min?: number;
  /** 입력값이 어디에 쓰이는지 한 줄 안내 */
  hint?: string;
}

type Resolved = { ok: boolean; value: string };

interface State {
  isOpen: boolean;
  options: (ConfirmOptions & Partial<PromptOptions>) | null;
  /** null 이면 확인 전용, 문자열이면 입력 모드 */
  input: string | null;
  resolver: ((value: Resolved) => void) | null;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

export const useConfirmStore = defineStore('confirmDialog', {
  state: (): State => ({ isOpen: false, options: null, input: null, resolver: null }),

  getters: {
    /** 입력이 규칙을 못 맞추면 사유 문자열, 통과면 null. */
    inputError(state): string | null {
      if (state.input === null || !state.options) return null;
      const v = state.input.trim();
      if (!v) return state.options.required ? 'Required.' : null;
      const t = state.options.inputType ?? 'text';
      if (t === 'date' && !DATE_RE.test(v)) return 'Use YYYY-MM-DD.';
      if (t === 'month' && !MONTH_RE.test(v)) return 'Use YYYY-MM.';
      if (t === 'number') {
        const n = Number(v);
        if (!Number.isFinite(n)) return 'Enter a number.';
        if (state.options.min != null && n < state.options.min) {
          return `Must be at least ${state.options.min}.`;
        }
      }
      return null;
    },
    canConfirm(): boolean {
      return (this as unknown as { inputError: string | null }).inputError === null;
    },
  },

  actions: {
    open(
      options: ConfirmOptions & Partial<PromptOptions>,
      withInput: boolean,
    ): Promise<Resolved> {
      return new Promise<Resolved>((resolve) => {
        this.options = {
          variant: 'primary',
          confirmLabel: 'Confirm',
          cancelLabel: 'Cancel',
          ...options,
        };
        this.input = withInput ? (options.initial ?? '') : null;
        this.resolver = resolve;
        this.isOpen = true;
      });
    },
    confirm(): void {
      if (!this.canConfirm) return;
      this.resolver?.({ ok: true, value: this.input ?? '' });
      this.cleanup();
    },
    cancel(): void {
      this.resolver?.({ ok: false, value: '' });
      this.cleanup();
    },
    cleanup(): void {
      this.isOpen = false;
      this.options = null;
      this.input = null;
      this.resolver = null;
    },
  },
});

/** `if (!(await useConfirm({ title, message }))) return;` */
export async function useConfirm(options: ConfirmOptions): Promise<boolean> {
  const { ok } = await useConfirmStore().open(options, false);
  return ok;
}

/** 취소는 null — 빈 문자열('입력 없이 진행')과 구분된다. */
export async function usePrompt(options: PromptOptions): Promise<string | null> {
  const { ok, value } = await useConfirmStore().open(options, true);
  return ok ? value.trim() : null;
}
